import { startOfHour, sub } from "date-fns";
import { alertFieldsFragment, incidentAlertFieldsFragment } from "gql/graphql";

/**
 * @returns an array of alerts that are deduped and sorted by created date (newest first)
 */
export const getFlatAlerts = (pages: alertFieldsFragment[][]) => {
	const seenAlertKeys = new Set<string>();
	const flatAlerts = pages.flat().filter((alert) => {
		const alertKey = `${alert.__typename}:${alert._id}`;
		if (seenAlertKeys.has(alertKey)) return false;
		seenAlertKeys.add(alertKey);
		return true;
	});

	// Sort alerts by created date in descending order (newest first)
	return flatAlerts.sort((a, b) => {
		const aCreated = a && "created" in a ? new Date(a.created).getTime() : 0;
		const bCreated = b && "created" in b ? new Date(b.created).getTime() : 0;
		return bCreated - aCreated;
	});
};

export const timeFilterAfterParam = (hours: number) =>
	sub(startOfHour(new Date()), { hours }).toISOString();

const hasCreated = (
	alert: alertFieldsFragment
): alert is Extract<alertFieldsFragment, { created: unknown }> =>
	alert && "created" in alert;

export const newestAlertCreated = (alerts: alertFieldsFragment[]) => {
	const newestWithCreated = alerts.find(hasCreated);
	return newestWithCreated && new Date(newestWithCreated.created);
};

export const oldestAlertCreated = (alerts: alertFieldsFragment[]) => {
	const oldestWithCreated = alerts.findLast(hasCreated);
	return oldestWithCreated && new Date(oldestWithCreated.created);
};

export function isIncidentAlert(
	alert: alertFieldsFragment
): alert is incidentAlertFieldsFragment {
	return alert.__typename === "IncidentAlert";
}

export type IncidentAlertWithUpdate = incidentAlertFieldsFragment & {
	incident_update: NonNullable<incidentAlertFieldsFragment["incident_update"]>;
};

/**
 * dashboard-api returns a null incident_update when tweet-event-service hides the
 * update from the account (Citizen-only incidents); those alerts have nothing to show.
 */
export function isIncidentAlertWithUpdate(
	alert: alertFieldsFragment
): alert is IncidentAlertWithUpdate {
	return isIncidentAlert(alert) && !!alert.incident_update;
}
